import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import {useRef} from 'react';
import ChartExportMenu, {EXPORT_IGNORE} from './ChartExportMenu';

export interface NumericPairLabels {
    uoa: string;
    average: string;
}

interface NumericPairProps {
    title: string;
    tooltip?: string;
    uoaValue: number | null;
    averageValue: number | null;
    isCount?: boolean; // true => raw number, false => percentage
    labels?: NumericPairLabels;
}

const fmt = (value: number | null, isCount?: boolean) => {
    if (value === null) {
        return '—';
    }

    // Averages come back as means of means, so trim the float noise before printing.
    const rounded = Math.round(value * 100) / 100;

    return isCount ? String(rounded) : `${rounded}%`;
};

const NumericPair = ({title, tooltip, uoaValue, averageValue, isCount, labels}: NumericPairProps) => {
    const chartRef = useRef<HTMLDivElement>(null);

    return (
        <Box ref={chartRef} sx={{border: '1px solid', borderColor: 'divider', borderRadius: 2.5, p: 2, boxShadow: 1, bgcolor: 'background.paper'}}>
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.5}}>
                <Typography variant='body2' color='text.secondary' sx={{flex: 1, lineHeight: 1.45, fontWeight: 500}}>
                    {title}
                </Typography>
                {tooltip && (
                    <Tooltip title={tooltip} placement='top' arrow>
                        <IconButton {...EXPORT_IGNORE} size='small' sx={{p: '2px', color: 'text.disabled', flexShrink: 0}}>
                            <InfoOutlinedIcon sx={{fontSize: 15}} />
                        </IconButton>
                    </Tooltip>
                )}
                <ChartExportMenu target={chartRef} title={title} />
            </Box>
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25}}>
                <Box sx={{bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5}}>
                    <Typography variant='caption' color='text.disabled'>
                        {labels?.uoa ?? 'Il vostro progetto'}
                    </Typography>
                    <Typography variant='h5' fontWeight={700} sx={{mt: 0.5}}>
                        {fmt(uoaValue, isCount)}
                    </Typography>
                </Box>
                <Box sx={{bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5}}>
                    <Typography variant='caption' color='text.disabled'>
                        {labels?.average ?? 'Media di tutti i progetti'}
                    </Typography>
                    <Typography variant='h5' fontWeight={700} sx={{mt: 0.5}}>
                        {fmt(averageValue, isCount)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default NumericPair;
